import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
} from "@chakra-ui/react";
import MemberFormFields, {
    buildEmptyMemberForm, buildMemberPayload, validateMemberForm,
} from "../forms/MemberFormFields";
import { addGroupMember } from "../groupsApi";

/**
 * Maps backend errors to friendly French messages.
 * @param {string} raw - Raw error
 * @returns {string}
 */
function mapAddError(raw) {
    if (typeof raw !== "string") return "Échec de l'ajout";
    if (raw.includes("Already member")) return "Cet email est déjà membre du groupe.";
    if (raw.includes("Missing email or surname")) return "Email et nom du membre sont requis.";
    return raw;
}

/**
 * Submits the create payload and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitAdd({ groupId, form, setLoading, onClose, onAdded }) {
    const err = validateMemberForm(form, true);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        const updated = await addGroupMember(groupId, buildMemberPayload(form));
        toast.success("Membre ajouté");
        onAdded?.(updated);
        onClose();
    } catch (error) {
        toast.error(mapAddError(error?.response?.data));
    } finally {
        setLoading(false);
    }
}

/**
 * Dialog allowing admins to add a member to a group.
 * @param {Object} props - { open, groupId, onClose, onAdded }
 */
function AddMemberDialog({ open, groupId, onClose, onAdded }) {
    const [form, setForm] = useState(buildEmptyMemberForm());
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setForm(buildEmptyMemberForm()); }, [open]);

    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submitAdd({ groupId, form, setLoading, onClose, onAdded });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Ajouter un membre</ModalHeader>
                <ModalCloseButton />
                <ModalBody><MemberFormFields form={form} onChange={onChange} isCreate /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Ajout...">Ajouter</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default AddMemberDialog;
